import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Clock, Filter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

interface Props {
  childId: string;
}

const TYPE_LABELS: Record<string, { label: string; emoji: string; color: string }> = {
  behavioral: { label: "سلوكي", emoji: "🤝", color: "#6366f1" },
  health: { label: "صحي", emoji: "🏃", color: "#22c55e" },
  creative: { label: "إبداعي", emoji: "🎨", color: "#ec4899" },
  social: { label: "اجتماعي", emoji: "🫂", color: "#14b8a6" },
};

export default function ActivityHistory({ childId }: Props) {
  const [activities, setActivities] = useState<any[]>([]);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    fetchHistory();
  }, [childId]);

  const fetchHistory = async () => {
    const { data } = await supabase
      .from("child_activities" as any)
      .select("*")
      .eq("child_id", childId)
      .order("completed_at", { ascending: false })
      .limit(50);
    setActivities((data || []) as any[]);
  };

  const filtered = filter === "all" ? activities : activities.filter((a: any) => a.activity_type === filter);

  // Pie chart data
  const typeCounts = activities.reduce((acc: Record<string, number>, a: any) => {
    acc[a.activity_type] = (acc[a.activity_type] || 0) + 1;
    return acc;
  }, {});
  const pieData = Object.entries(typeCounts).map(([key, value]) => ({
    name: TYPE_LABELS[key]?.label || key,
    value,
    color: TYPE_LABELS[key]?.color || "#888",
  }));

  return (
    <Card className="border-2 border-muted">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5" /> سجل الأنشطة
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pie chart */}
        {pieData.length > 0 && (
          <div className="flex items-center gap-4">
            <div className="w-28 h-28">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={50} innerRadius={20}>
                    {pieData.map((d, i) => (
                      <Cell key={i} fill={d.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, name) => [`${String(v)} نشاط`, String(name)]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2">
              {pieData.map(d => (
                <div key={d.name} className="flex items-center gap-1 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                  <span>{d.name}: {String(d.value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")}>
            <Filter className="h-3 w-3 ml-1" /> الكل
          </Button>
          {Object.entries(TYPE_LABELS).map(([key, { label, emoji }]) => (
            <Button key={key} size="sm" variant={filter === key ? "default" : "outline"} onClick={() => setFilter(key)}>
              {emoji} {label}
            </Button>
          ))}
        </div>

        {/* List */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">لا توجد أنشطة بعد</p>
          ) : (
            filtered.map((a: any) => {
              const type = TYPE_LABELS[a.activity_type] || { label: a.activity_type, emoji: "📋", color: "#888" };
              return (
                <div key={a.id} className="flex items-center gap-3 p-2 rounded-lg border bg-card">
                  <span className="text-xl">{type.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{a.activity_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(a.completed_at).toLocaleDateString("ar-SA")}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <span className="font-bold text-foreground">{a.score}</span>
                    <span className="text-muted-foreground">نقطة</span>
                  </div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3].map(s => (
                      <Star key={s} className={`h-3.5 w-3.5 ${s <= (a.stars || 0) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
